import React, { useMemo, useState } from "react";
import { AutoComplete, Button, Form, Input } from "antd";
import { ShieldCheck } from "lucide-react";
import styled from "styled-components";
import { Icon } from "@/shared/components/Icon/Icon";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { ALL_BANK_NAMES } from "@/features/creator/constants/banksByCountry";

export interface BankDetailsValues {
	country: string;
	bankName: string;
	accountName: string;
	accountNumber: string;
	swiftBic?: string;
	currency?: string;
}

interface BankDetailsStepProps {
	onSubmit: (values: BankDetailsValues) => void;
	loading?: boolean;
}

const getDefaultCountry = () => {
	const localeRegion = navigator.language.match(/[-_]([A-Za-z]{2})$/)?.[1];
	return localeRegion?.toUpperCase() || "AU";
};

/** Figma: thick stroke chevron ~14×8 */
const ChevronDownIcon = () => (
	<svg
		width="14"
		height="8"
		viewBox="0 0 14 8"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden
		className="bank-name-chevron"
	>
		<path
			d="M1.4 1.4L7 6.8L12.6 1.4"
			stroke="#303030"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const Heading = styled.div`
	/* Figma: ~12px from description block to security notice */
	margin-bottom: 12px;
`;

const WalletIcon = styled(Icon)`
	/* Figma: ~24px from icon bottom to title */
	margin-bottom: 24px;
`;

const Title = styled.h1`
	/* Figma: ~20px between title and description line boxes */
	margin: 0 0 20px;
	color: #303030;
	font-size: 24px;
	font-weight: 600;
	line-height: 1.2;
`;

const Description = styled.p`
	margin: 0;
	color: #303030;
	font-size: 16px;
	font-weight: 400;
	line-height: 1.5;
`;

const SecurityNotice = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	min-height: 54px;
	margin-bottom: 20px;
	padding: 14px 16px;
	border: 1px solid #dcdef8;
	border-radius: 12px;
	background: #f3f5ff;
	color: #303030;
	font-size: 14px;
	line-height: 1.4;

	svg {
		width: 22px;
		height: 22px;
		flex: 0 0 auto;
		color: ${CUSTOM_COLORS.PRIMARY};
	}
`;

/* Label / input styles mirror AddBankDetailsModal's FormWrap */
const BankDetailsForm = styled(Form<BankDetailsValues>)`
	text-align: left;

	.ant-form-item {
		margin-bottom: 20px !important;
	}

	/* When an error shows, the 20px gap is provided by the error slot instead of
	   the margin — the error sits 4px under its input (same as Add Bank modal)
	   and total height never changes, so nothing shifts or scrolls. */
	.ant-form-item.ant-form-item-with-help {
		margin-bottom: 0 !important;
	}

	/* Neutralize antd's help-transition negative margin, which pulls the next field up */
	.ant-form-item-margin-offset {
		margin-bottom: 0 !important;
	}

	.ant-form-item-explain {
		height: 20px;
		min-height: 20px !important;
		box-sizing: border-box;
		padding-top: 4px;
		overflow: hidden;
	}

	.ant-form-item-explain-error {
		font-size: 12px;
		line-height: 1.35;
		color: #ff4d4f;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.ant-form-item-label {
		padding: 0 !important;
	}

	.ant-form-item-label > label {
		color: #707070 !important;
		font-size: 14px;
		font-weight: 400 !important;
		height: auto !important;
		line-height: 1.2 !important;
		padding-bottom: 4px !important;
	}

	.ant-form-item-control-input {
		min-height: 40px;
	}

	/* AutoComplete shell is transparent — the inner Input carries the border (same as modal) */
	.ant-select.ant-select-auto-complete {
		display: block;
		width: 100%;
		height: 40px !important;
	}

	.ant-select-auto-complete .ant-select-selector {
		height: 40px !important;
		padding: 0 !important;
		border: none !important;
		background: transparent !important;
		box-shadow: none !important;
		display: block !important;
	}

	.ant-select-auto-complete .ant-select-selection-search {
		inset: 0 !important;
		display: block !important;
	}

	.ant-input-affix-wrapper,
	.ant-input {
		border: 1px solid #d2d3e5 !important;
		border-radius: 8px !important;
		background: #fff !important;
		box-shadow: none !important;
		font-size: 14px;
		font-weight: 400;
		color: #303030;
	}

	.ant-input-affix-wrapper {
		/* Figma: chevron ink sits ~16px inside the right border */
		padding: 0 16px 0 12px !important;
		height: 40px !important;
		min-height: 40px !important;
		align-items: center;
		box-sizing: border-box;
		display: flex !important;

		.ant-input {
			height: 38px !important;
			padding: 0 !important;
			border: none !important;
			box-shadow: none !important;
			background: transparent !important;
			color: #303030;
		}

		.ant-input-suffix {
			display: inline-flex;
			align-items: center;
			gap: 8px;
			margin-inline-start: 8px;
			color: #303030;

			svg {
				display: block;
				flex-shrink: 0;
			}
		}

		.ant-input-clear-icon {
			margin: 0;
			font-size: 12px;
			display: inline-flex;
			align-items: center;
		}

		.bank-name-chevron {
			pointer-events: none;
		}
	}

	.ant-input:not(.ant-input-affix-wrapper .ant-input) {
		height: 40px !important;
		padding: 0 12px !important;
	}

	.ant-input::placeholder {
		color: #a0a0a8;
	}

	.ant-input-affix-wrapper:hover,
	.ant-input-affix-wrapper-focused,
	.ant-input:hover,
	.ant-input:focus,
	.ant-input-focused {
		border-color: #c5cdf8 !important;
		box-shadow: none !important;
	}
`;

const Actions = styled.div`
	/* item margin 20px + 12px = 32px before button, same as modal */
	margin-top: 12px;
	margin-bottom: 20px;

	@media (max-width: 640px) {
		margin-top: 8px;
	}
`;

const ActionButton = styled(Button)`
	width: 100%;
	height: 56px;
	border-radius: 999px;
	font-size: 16px;
	font-weight: 600;
	box-shadow: none;

	&:disabled,
	&.ant-btn-disabled {
		border-color: #d9d9d9 !important;
		color: #8c8c8c !important;
		background: #f5f5f5 !important;
		cursor: not-allowed;
		opacity: 1 !important;
		box-shadow: none !important;
	}

	@media (max-width: 640px) {
		height: 48px;
	}
`;

const PrimaryButton = styled(ActionButton)`
	background: ${CUSTOM_COLORS.PRIMARY};
	border-color: ${CUSTOM_COLORS.PRIMARY};

	&:hover,
	&:focus {
		background: ${CUSTOM_COLORS.PRIMARY} !important;
		border-color: ${CUSTOM_COLORS.PRIMARY} !important;
		opacity: 0.92;
	}
`;

const BankDetailsStep: React.FC<BankDetailsStepProps> = ({ onSubmit, loading = false }) => {
	const [form] = Form.useForm<BankDetailsValues>();
	const [bankSearch, setBankSearch] = useState("");
	const bankOptions = useMemo(() => {
		const q = bankSearch.trim().toLowerCase();
		const filtered = q ? ALL_BANK_NAMES.filter((name) => name.toLowerCase().includes(q)) : ALL_BANK_NAMES;
		return filtered.map((value) => ({ value }));
	}, [bankSearch]);

	return (
		<BankDetailsForm
			form={form}
			layout="vertical"
			requiredMark={false}
			onFinish={onSubmit}
			initialValues={{ country: getDefaultCountry() }}
			// This step renders inside Register's outer <Form>. Take full control of the
			// submit event: block the native submission (which would reload/navigate),
			// keep it away from the outer form, and run this form's validation instead.
			onSubmitCapture={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.submit();
			}}
		>
			<Heading>
				<WalletIcon src="/icon/wallet.svg" size={30} alt="" />
				<Title>Set up your bank details</Title>
				<Description>Add your bank details so we can send your payout once the campaign wraps.</Description>
			</Heading>

			<SecurityNotice>
				<ShieldCheck aria-hidden="true" />
				<span>Data is encrypted in transit and passed to the Finance Team.</span>
			</SecurityNotice>

			<Form.Item name="country" hidden>
				<Input />
			</Form.Item>
			<Form.Item name="bankName" label="Bank Name" rules={[{ required: true, message: "Bank name is required" }]}>
				<AutoComplete
					options={bankOptions}
					onSearch={setBankSearch}
					filterOption={false}
					defaultActiveFirstOption={false}
					popupMatchSelectWidth
				>
					<Input allowClear autoComplete="off" suffix={<ChevronDownIcon />} />
				</AutoComplete>
			</Form.Item>
			<Form.Item
				name="accountName"
				label="Account Name"
				rules={[{ required: true, message: "Account name is required" }]}
			>
				<Input />
			</Form.Item>
			<Form.Item
				name="accountNumber"
				label="Account Number"
				rules={[{ required: true, message: "Account number is required" }]}
			>
				<Input autoComplete="off" />
			</Form.Item>
			<Form.Item name="swiftBic" label="BIC/SWIFT Code">
				<Input />
			</Form.Item>
			<Actions>
				<PrimaryButton type="primary" htmlType="submit" loading={loading}>
					Save &amp; Continue
				</PrimaryButton>
			</Actions>
		</BankDetailsForm>
	);
};

export default BankDetailsStep;
